import React from 'react';
import {
  DropboxIntegration, UploadIntegrationType,
} from 'hellospa/page/prep-and-send/data/types/integration';
import ReAuthModal from './reauth-modal';
import dropboxIcon from 'images/external_logo_med_D.png';
import { useLatestRef } from 'hellospa/common/hooks';
import delay from 'hellospa/common/utils/delay';

type DropboxFile = {
  link: string,
  name: string,
}

type PickerOptions = {
  cancel: () => void,
  linkType: string,
  iframe: boolean,
  success: (files: DropboxFile[]) => void,
}

type Dropbox = {
  choose: (options: PickerOptions) => void,
}

declare global {
  interface Window { Dropbox: Dropbox; }
}

type Props = {
  settings: DropboxIntegration,
  shouldTriggerPicker: boolean,
  onSuccess: (id: string, name: string) => void
  toggleIntegrationPicker: (integration: string, status: boolean) => void,
}

const SCRIPT_SRC = 'https://www.dropbox.com/static/api/2/dropins.js';
const SCRIPT_ID = 'dropboxjs';
/**
 * Load the script to inject into the DOM (happens on component mount)
 * @param  {DropboxIntegration} settings
 * @return {void}
 */
function initialize(settings: DropboxIntegration) {
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  script.setAttribute('nonce', settings.nonce);
  script.setAttribute('data-app-key', settings.key);

  return script;
}

const chooseDropboxFile = () => new Promise<DropboxFile>(async (resolve, reject) => {
  while (window.Dropbox == null) {
    // eslint-disable-next-line no-await-in-loop
    await delay(100);
  }

  const options: PickerOptions = {
    linkType: 'direct',
    iframe: true,

    success: (files: DropboxFile[]) => {
      // select the first value since multi select is disabled currently
      const file = files.length > 0 ? files[0] : null;

      if (file) {
        resolve(file);
      }
      reject();
    },

    cancel: () => {
      reject();
    },
  };

  console.log('choose');
  // initialize the Dropbox chooser (file picker)
  window.Dropbox.choose(options);
});

export default function DropboxFilePicker({
  settings,
  shouldTriggerPicker,
  toggleIntegrationPicker,
  onSuccess,
}: Props) {

  React.useEffect(() => {
    const script = initialize(settings);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [settings]);

  // NOTE: The useEffect needs to be stable, meaning it only runs the effect
  // when `settings.enabled` or `shouldTriggerPicker` change. onSuccess is not
  // stable on its own unless the parent wraps it in `useCallback`. By using a
  // ref here the callback can continue to be updated without triggering
  // useEffect. If you want to see the bug this fixes, add `onSuccess` to the
  // dependency array.  If this re-renders while the picker is open, it'll
  // flicker beause it'll open a new picker.
  const successRef = useLatestRef(onSuccess);
  React.useEffect(() => {
    if (shouldTriggerPicker && settings.enabled) {
      chooseDropboxFile()
        .then((file) => successRef.current(file.link, file.name))
        .finally(() => {
          toggleIntegrationPicker(UploadIntegrationType.Dropbox, false);
        });
    }
  }, [settings.enabled, shouldTriggerPicker, successRef, toggleIntegrationPicker]);

  // if clicked on the integration icon but it's not enabled,
  // trigger the modal to initiate auth
  if (shouldTriggerPicker && !settings.enabled) {
    return (
      <ReAuthModal
        integration={UploadIntegrationType.Dropbox}
        integrationIcon={dropboxIcon}
        isOpen={shouldTriggerPicker && !settings.enabled}
        onClose={() => toggleIntegrationPicker(UploadIntegrationType.Dropbox, false)} />
    );
  }

  return null;
}
